import type { IChartApi, SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type {
  OppOpenSpreadDeviationCycle,
  OppSpreadDeviationCycle,
} from '@/services/oppDetectorApi';

export type SpreadPoint = { time: UTCTimestamp; value: number };

export const toIntervalBucketTs = (ts: number, intervalSeconds: number): UTCTimestamp => {
  const safeInterval = Math.max(1, Math.floor(intervalSeconds));
  return (Math.floor(ts / safeInterval) * safeInterval) as UTCTimestamp;
};

export const msToChartTime = (tsMs: number, intervalSeconds: number): UTCTimestamp =>
  toIntervalBucketTs(Math.floor(tsMs / 1000), intervalSeconds);

const findNearestSpreadValue = (series: SpreadPoint[], tsMs: number, intervalSeconds: number): number | null => {
  if (series.length === 0) return null;
  const target = msToChartTime(tsMs, intervalSeconds);
  let best: SpreadPoint | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const point of series) {
    const distance = Math.abs(Number(point.time) - Number(target));
    if (distance < bestDistance) {
      bestDistance = distance;
      best = point;
    }
  }
  return best?.value ?? null;
};

export function buildSpreadDeviationCycleMarkers(
  cycles: OppSpreadDeviationCycle[],
  openCycle: OppOpenSpreadDeviationCycle | null | undefined,
  intervalSeconds: number,
  markerSeries: SpreadPoint[],
  options?: { includePeakMarker?: boolean },
): SeriesMarker<Time>[] {
  const includePeakMarker = options?.includePeakMarker ?? true;
  const markers: SeriesMarker<Time>[] = [];

  for (const cycle of cycles) {
    const startTime = msToChartTime(cycle.start_ms, intervalSeconds);
    const endTime = msToChartTime(cycle.end_ms, intervalSeconds);
    const peakTime = msToChartTime(cycle.peak_ms, intervalSeconds);
    const peakPrice =
      findNearestSpreadValue(markerSeries, cycle.peak_ms, intervalSeconds) ?? cycle.peak_spread;

    markers.push({
      time: startTime,
      position: 'belowBar',
      shape: 'arrowUp',
      color: '#7c3aed',
      text: `S${cycle.cycle_index + 1}`,
      size: 1,
    });

    if (includePeakMarker && Number.isFinite(peakPrice)) {
      markers.push({
        time: peakTime,
        position: 'atPriceMiddle',
        shape: 'circle',
        color: '#ea580c',
        price: peakPrice,
        size: 1.2,
      });
    }

    markers.push({
      time: endTime,
      position: 'belowBar',
      shape: 'arrowDown',
      color: '#059669',
      text: `E${cycle.cycle_index + 1}`,
      size: 1,
    });
  }

  if (openCycle) {
    markers.push({
      time: msToChartTime(openCycle.start_ms, intervalSeconds),
      position: 'belowBar',
      shape: 'arrowUp',
      color: '#c4b5fd',
      text: 'S*',
      size: 1,
    });
  }

  return markers.sort((a, b) => Number(a.time) - Number(b.time));
}

export function attachSpreadDeviationCycleOverlay(
  container: HTMLDivElement,
  chart: IChartApi,
  getModel: () => {
    cycles: OppSpreadDeviationCycle[];
    openCycle: OppOpenSpreadDeviationCycle | null | undefined;
    intervalSeconds: number;
    lastTimeMs: number | null;
  },
): { dispose: () => void; render: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'spread-deviation-cycle-overlay';
  container.appendChild(overlay);

  const render = () => {
    const { cycles, openCycle, intervalSeconds, lastTimeMs } = getModel();
    const spreadPane = chart.panes()[0];
    overlay.style.height = `${spreadPane?.getHeight() ?? Math.floor(container.clientHeight * 0.5)}px`;
    overlay.replaceChildren();

    const timeScale = chart.timeScale();
    const addBand = (startMs: number, endMs: number, className: string) => {
      const startTime = msToChartTime(startMs, intervalSeconds) as Time;
      const endTime = msToChartTime(endMs, intervalSeconds) as Time;
      const x1 = timeScale.timeToCoordinate(startTime);
      const x2 = timeScale.timeToCoordinate(endTime);
      if (x1 == null || x2 == null) return;
      const left = Math.min(x1, x2);
      const width = Math.max(2, Math.abs(x2 - x1));
      const band = document.createElement('div');
      band.className = className;
      band.style.left = `${left}px`;
      band.style.width = `${width}px`;
      overlay.appendChild(band);
    };

    for (const cycle of cycles) {
      addBand(cycle.start_ms, cycle.end_ms, 'spread-deviation-cycle-band');
    }
    if (openCycle && lastTimeMs != null && lastTimeMs > openCycle.start_ms) {
      addBand(openCycle.start_ms, lastTimeMs, 'spread-deviation-cycle-band is-open');
    }
  };

  chart.timeScale().subscribeVisibleLogicalRangeChange(render);
  chart.timeScale().subscribeSizeChange(render);
  const resizeObserver = new ResizeObserver(render);
  resizeObserver.observe(container);
  render();

  return {
    render,
    dispose: () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(render);
      chart.timeScale().unsubscribeSizeChange(render);
      resizeObserver.disconnect();
      overlay.remove();
    },
  };
}
